import React, { useState, useEffect } from 'react';
import { BusinessPlanSection, SectionId } from '../../types/businessPlan';

interface BusinessPlanSectionEditorProps {
  section: BusinessPlanSection;
  onSave: (sectionId: SectionId, content: string) => Promise<boolean>;
  onCancel: () => void;
  isLoading?: boolean;
}

const BusinessPlanSectionEditor: React.FC<BusinessPlanSectionEditorProps> = ({
  section,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const [content, setContent] = useState<string>(section.content || '');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Update content if section changes
    setContent(section.content || '');
  }, [section]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    // Clear any previous errors
    if (error) setError(null);
  };

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Content cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      const success = await onSave(section.id, content);
      if (!success) {
        setError('Failed to save changes. Please try again.');
      }
    } catch (err) {
      console.error('Error saving section:', err);
      setError('An unexpected error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-4">Edit {section.title}</h2>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      <div className="mb-4">
        <label htmlFor="section-content" className="block text-sm font-medium text-gray-700 mb-2">
          Content
        </label>
        <textarea
          id="section-content"
          rows={20}
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          value={content}
          onChange={handleContentChange}
          disabled={isSaving || isLoading}
        />
      </div>
      
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isSaving || isLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isSaving || isLoading}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default BusinessPlanSectionEditor;
